import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { useAppStore } from "@/store/use-app-store";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

const CSRF_SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

// Fetch and cache the CSRF token from /api/auth/me when it hasn't been
// loaded into the store yet (e.g. on first page load before AuthSessionSync
// completes, or after a hard refresh on mobile). Keeps a single in-flight
// promise so concurrent callers share the same request.
let _csrfFetchPromise: Promise<string | null> | null = null;

async function ensureCsrfToken(): Promise<string | null> {
  const existing = useAppStore.getState().csrfToken;
  if (existing) return existing;

  // Deduplicate concurrent fetches
  if (!_csrfFetchPromise) {
    _csrfFetchPromise = fetch("/api/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return null;
        const body = await res.json();
        if (body.csrfToken) {
          useAppStore.getState().setCsrfToken(body.csrfToken);
        }
        return body.csrfToken ?? null;
      })
      .catch(() => null)
      .finally(() => { _csrfFetchPromise = null; });
  }

  return _csrfFetchPromise;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";

  // Attach the per-session CSRF token on every state-changing request. The
  // server only enforces it on admin endpoints (via `requireAdmin`), but
  // sending it on every non-safe method is harmless and keeps callers from
  // having to remember which routes need it.
  // If the token hasn't been loaded yet (page just refreshed), fetch it now
  // so the request doesn't fail with a 403 due to a missing CSRF header.
  if (!CSRF_SAFE_METHODS.has(method.toUpperCase())) {
    const token = await ensureCsrfToken();
    if (token) headers["X-CSRF-Token"] = token;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
